import { Content } from 'pleinchamp-api-client';
import type { ReactNode } from 'react';
import React, { ComponentProps, FC, PropsWithChildren, useCallback, useContext, useMemo } from 'react';
import { ADVERT_FORMAT_ID } from '@adverts/business/Adverts.contants';
import { insertAdvertInArray } from '@adverts/business/adverts.utils';
import { PlcApiResponseInterface } from '@api/business/api.hook';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { PlcMessageWithRetry } from '@components/PlcMessage/PlcMessageWithRetry.component';
import { Text } from '@components/Text/Text.component';
import { SearchContentType } from '@search/business/search.types';
import { Spacing } from '@styles/Spacing.style';
import { getAllContentTypeLinkProps } from '@utils/url';
import classnames from 'classnames';
import { GeneralContext } from 'features/business/app.contexts';
import styles from './LastContent.module.scss';

const LastContentLoader: FC<{ uniqueKey: string }> = ({ uniqueKey }) => (
  <PlcTextListLoader
    textProps={[
      { bottomSpacing: Spacing.s, variant: 'h6', width: 75 },
      { variant: 'h4', width: 200 },
    ]}
    uniqueKey={`lastContent-${uniqueKey}`}
    width={300}
  />
);

type Props = ComponentProps<'div'> & {
  cards: ReactNode[];
  seeAllLinkI18nKey?: string;
  titleI18nKey?: string;
  contentType?: SearchContentType;
} & Omit<PlcApiResponseInterface<Content[]>, 'data'>;

export const LastContent: FC<PropsWithChildren<Props>> = ({
  cards,
  isValidating,
  error,
  retryCallback,
  titleI18nKey,
  seeAllLinkI18nKey,
  contentType,
  className,
  children,
  ...rest
}) => {
  const cardsWithAd = useMemo(() => insertAdvertInArray(cards, 2, 3, ADVERT_FORMAT_ID.SmallNative), [cards]);
  const allHref = contentType && getAllContentTypeLinkProps(contentType);

  const { setSearchQuery } = useContext(GeneralContext);
  const resetSearchQuery = useCallback(() => setSearchQuery?.(''), [setSearchQuery]);

  const rootClassName = classnames(`${styles.lastContent} 'flex column'`, className);

  return (
    <div className={rootClassName} {...rest}>
      <Text className="plc-mb-basis" i18nKey={titleI18nKey} tag="p" variant="p-h3-upper-style" />
      <div className={`${styles.lastContentList} 'border-bottom-up-m plc-mb-up-m-basis'`}>
        {isValidating ? (
          <>
            <LastContentLoader uniqueKey="1" />
            <LastContentLoader uniqueKey="2" />
            <LastContentLoader uniqueKey="3" />
          </>
        ) : error ? (
          <PlcMessageWithRetry retryCallback={retryCallback} />
        ) : (
          cardsWithAd
        )}
      </div>
      {children && <div className="border-bottom-up-m plc-mb-up-m-basis">{children}</div>}
      {seeAllLinkI18nKey && allHref && (
        <BasicLink
          href={allHref}
          isUnderlined
          onClick={resetSearchQuery}
          textProps={{ flavour: 'meteo-dark-green', i18nKey: seeAllLinkI18nKey, variant: 'h6' }}
        />
      )}
    </div>
  );
};
